import { faCircleDot, faServer } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Hotkey, useHotkey, useKeyHold } from '@tanstack/react-hotkeys';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUserSettingsStore } from 'contexts/user_settings/UserSettingsStore';
import { usePopup } from 'custom_hooks/usePopup';
import { useCallback, useMemo, useState } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { MenuButton } from 'styles/theme_styles/button';
import { pointerDownClickHandler } from 'utils/pointer_events';

import { ShortcutBadge } from '../hotkey_display/ShortcutBadge';

type EnvInfo = {
    url: string;
    name: string;
    hotkey: Hotkey;
};

const SD4_ENVS: Record<string, EnvInfo> = {
    localhost: {
        url: 'http://localhost:3000',
        name: 'localhost',
        hotkey: 'L',
    },
    stage01: {
        url: 'https://sdstage01.kcftech.com',
        name: 'stage01',
        hotkey: '1',
    },
    stage02: {
        url: 'https://sdstage02.kcftech.com',
        name: 'stage02',
        hotkey: '2',
    },
    prod: {
        url: 'https://sd.kcftech.com',
        name: 'prod',
        hotkey: 'P',
    },
    // labs01: 'https://sdlabs.kcftech.com',
    // dev03: 'https://sddev03.kcftech.com',
    // dev04: 'https://sddev04.kcftech.com',
};

type Environment = keyof typeof SD4_ENVS;
function classifyHost(host: string): Environment | 'other' {
    if (host.includes('sd.kcftech.com')) {
        return 'prod';
    }
    if (host.includes('stage01')) {
        return 'stage01';
    }
    if (host.includes('stage02')) {
        return 'stage02';
    }
    if (host.includes('localhost')) {
        return 'localhost';
    }
    return 'other';
}

const EnvList = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.2rem;
    min-width: 14rem;
    border-top: 2px solid var(--primary-default);
`;

const EnvLink = styled.a`
    padding: 0.4rem 0.8rem;
    text-decoration: none;
    color: var(--primary-default);
    border-radius: 0.3rem;
    font-size: 1.4rem;

    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
        text-decoration: underline;
        filter: brightness(80%);
    }
`;

export function EnvSwitcherIcon() {
    return <FontAwesomeIcon icon={faServer} />;
}

type EnvRowProps = {
    envName: string;
    baseUrl: string;
    isCurrent: boolean;
    pathname: string;
    search: string;
    hotkey: Hotkey;
    showHotkey: boolean;
    navigateToEnv: (env: string) => void;
};

function EnvRow({
    envName,
    baseUrl,
    isCurrent,
    pathname,
    search,
    hotkey,
    showHotkey,
    navigateToEnv,
}: EnvRowProps) {
    useHotkey(hotkey, () => navigateToEnv(baseUrl));

    return (
        <EnvLink href={baseUrl + pathname + search}>
            {isCurrent && <FontAwesomeIcon icon={faCircleDot} />}
            {isCurrent ? (
                <strong style={{ color: 'var(--text-secondary)' }}>{envName}</strong>
            ) : (
                envName
            )}
            <ShortcutBadge hotkey={hotkey} show={showHotkey} />
        </EnvLink>
    );
}

export function EnvironmentSwitcherPopup() {
    const { canSwitchEnvironment } = usePermissions();
    const isEnabled = useUserSettingsStore((state) => state.envSwitcher);
    const currentEnvironment = useMemo(() => classifyHost(window.location.host), []);

    const isModHeld = useKeyHold('Control');
    const [openedWithMod, setOpenedWithMod] = useState(false);

    const { isOpen, toggleOpen, close, triggerRef, popupRef } = usePopup<
        HTMLButtonElement,
        HTMLDivElement
    >({
        closeOnClickOutside: true,
        onStateChange: ({ open }) => {
            if (!open) {
                setOpenedWithMod(false);
            }
        },
    });
    useHotkey('Escape', close, { preventDefault: false, stopPropagation: false });

    const { pathname, search } = useLocation();

    const navigate = useCallback(
        (env: string) => {
            window.location.href = window.location.href.replace(
                SD4_ENVS[currentEnvironment].url,
                env
            );
        },
        [currentEnvironment]
    );

    useHotkey(
        'Mod+E',
        () => {
            toggleOpen();
            if (!isOpen) {
                setOpenedWithMod(isModHeld);
            }
        },
        {
            requireReset: true,
        }
    );

    if (!canSwitchEnvironment || !isEnabled) {
        return null;
    }
    const handlers = pointerDownClickHandler(toggleOpen);

    return (
        <>
            <MenuButton
                aria-label="Environment Switcher"
                title="Environment Switcher"
                onPointerDown={handlers.onPointerDown}
                onPointerUp={handlers.onPointerUp}
                onClick={handlers.onClick}
                $active={isOpen}
                ref={triggerRef}
                style={{ position: 'relative' }}
            >
                <FontAwesomeIcon icon={faServer} />
                <ShortcutBadge
                    hotkey="Mod+E"
                    show={isModHeld || openedWithMod}
                    style={{
                        position: 'absolute',
                        bottom: '0rem',
                        left: '50%',
                        transform: 'translate(-50%, -4px)',
                    }}
                />
            </MenuButton>
            {isOpen && (
                <StatelessPopup
                    noPadding
                    onClose={close}
                    anchor={triggerRef.current}
                    ariaLabel="Environment Switcher"
                    ref={popupRef}
                    noOverlay
                >
                    <ReactFocusLock>
                        <EnvList>
                            {Object.entries(SD4_ENVS).map(([envKey, info]) => (
                                <EnvRow
                                    key={envKey}
                                    envName={info.name}
                                    baseUrl={info.url}
                                    isCurrent={currentEnvironment === envKey}
                                    hotkey={info.hotkey}
                                    showHotkey={isModHeld || openedWithMod}
                                    navigateToEnv={navigate}
                                    pathname={pathname}
                                    search={search}
                                />
                            ))}
                        </EnvList>
                    </ReactFocusLock>
                </StatelessPopup>
            )}
        </>
    );
}
